export interface IPostMessageInfo<T> {
  readonly name: string;
  readonly data: T;
}

export interface IPostMessageListener {
  handler: IPostMessageHandler;
  origin?: string;
}

type IPostMessageHandler = (data: any) => void;

class PostMessage {
  events: Record<string, Array<IPostMessageListener> | undefined> = {};

  onMessage = (ev: MessageEvent<IPostMessageInfo<string>>) => {
    const info = ev.data;
    if (!info?.name) {
      return;
    }
    const { name, data } = info;
    const listeners = this.events[name] || [];
    listeners.forEach((listener) => {
      if (!listener.origin || ev.origin?.startsWith(listener.origin)) {
        listener.handler(data);
      }
    });
  };

  on(name: string, handler: IPostMessageHandler, origin?: string) {
    if (this.isEventEmpty()) {
      window.addEventListener('message', this.onMessage);
    }
    const handlers = this.events[name];
    if (!handlers) {
      this.events[name] = [{ handler, origin }];
    } else {
      handlers.push({ handler, origin });
    }
  }

  off(name: string, handler?: IPostMessageHandler, origin?: string) {
    const listeners = this.events[name];
    if (!listeners) {
      return;
    }
    if (!handler) {
      this.events[name] = undefined;
      return;
    }
    const matchedIndexes = listeners.reduce<number[]>(
      (acc, listener, index) => {
        if (listener.origin === origin && listener.handler === handler) {
          acc.push(index);
        }
        return acc;
      },
      []
    );

    this.events[name] = listeners.filter(
      (listener, index) => !matchedIndexes.includes(index)
    );

    if (this.isEventEmpty()) {
      window.removeEventListener('message', this.onMessage);
    }
  }

  isEventEmpty() {
    const eventKeys = Object.keys(this.events);
    return (
      !eventKeys.length ||
      eventKeys.every((key) => !this.events[key] || !this.events[key]?.length)
    );
  }
}

export const windowPostMessage = new PostMessage();
